import { defineComponent as _defineComponent } from 'vue'
import { PropType, ref, Ref, computed, onMounted, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { shuffleItems, playSounds, stopSounds } from '@/utilities'
import { useCMSStore, CMSStoreType } from '@/templates/slplus/store/useCMSStore'
import { useGameStore } from '@/store/useGameStore'
import { useUserStore } from '@/store/useUserStore'
import { useDeviceService } from '@/composition/useDevice'
import { Day } from '@/templates/slplus/structure/generalModels'
import { Sevenseas, SevenseasItem, TASK_TYPES } from '@/templates/slplus/structure/questionModels'
import { Player, TRACKING_TYPE, TrackingDetails } from '@/types/main'
import Button from '@/components/base/Button.vue'
import Avatar from '@/components/base/Avatar.vue'

import Intro from '@/components/task/Intro.vue'
import PlayerReady from '@/components/task/sevenseas/PlayerReady.vue'
import Fishing from '@/components/task/sevenseas/Fishing.vue'
import ItemDetail from '@/components/task/sevenseas/ItemDetail.vue'
import DiscussionTime from '@/components/task/sevenseas/DiscussionTime.vue'

import boatImage from '@/assets/tasks/sevenseas/Omar-boat1.svg'

import trudeluttSound from '@/assets/tasks/stovnerfaret_26_10.mp3'
import introSound from '@/assets/tasks/sevenseas/syvhav_intro_S1.mp3'
import yourTurn from '@/assets/tasks/sevenseas/syvhav_tur_S2.mp3'

enum MODES {
  intro = 'intro',
  playerReady = 'playerReady',
  fishing = 'fishing',
  quizz = 'quizz',
  wordFocus = 'wordFocus',
  discussion = 'discussion',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Sevenseas',
  props: {
  question: { required: true, type: Object as PropType<Sevenseas> },
  myIndex: { required: false, type: Number, default: 0 },
},
  emits: ['start-introduction', 'completed', 'not-completed'],
  setup(__props, { expose, emit }) {
  expose();

const props = __props

const messages = {
  no: {
    title: 'Omar på de syv hav',
    description: 'Nå skal vi ut på fisketur og snakke om det vi finner. Dere skal alle få hjelpe Omar med å fiske.',
    getStarted: 'Start',
    exercise: 'Aktivitet ',
    yourTurn: 'det er din tur!',
    imReady: 'Jeg er klar!',
    letsDiscuss: 'La oss snakke sammen',
  },
  en: {
    title: 'Omar on the seven seas',
    description: 'Now we shall go fishing and speak about what we find. You shall all help Omar to fish.',
    getStarted: 'Start',
    exercise: 'Exercise ',
    yourTurn: 'it is your turn!',
    imReady: 'I am ready',
    letsDiscuss: "Let's all talk about it",
  },
}



const { t } = useI18n({ messages })
const { question } = toRefs(props)
const { getters: deviceGetters, actions: deviceActions } = useDeviceService()
const mode: Ref<MODES> = ref(MODES.intro)
const cmsStore: CMSStoreType = useCMSStore()
const gameStore = useGameStore()
const userStore = useUserStore()
const selectedSet: Day = cmsStore.getters.selectedSet.value.set as Day
const word = selectedSet ? selectedSet.word : ''
const items: Ref<SevenseasItem[]> = ref([])
const currentItem: Ref<SevenseasItem | undefined> = ref()
const players = gameStore.getters.playersInSelectedGame
const shuffledPlayers = shuffleItems(players.value)
const currentPlayer: Ref<Player | undefined> = ref()
let currentTracking: TrackingDetails | undefined
const introHeard = ref(false)

// Each time the task is run, shuffle the players
onMounted(() => {
  introHeard.value = false
  currentPlayer.value = undefined
})

// Initial setup
if (question.value) {
  // Create a shuffled list of items to display
  items.value = shuffleItems(question.value.items)
  // Configure the parent with this Question's introduction audio
  // context.emit('setup-audio-introduction', '/assets/sounds/tasks/sevenseas/intro.mp3')
  playSounds([trudeluttSound, introSound])
}

const exerciseIndex = computed(() => `${t('exercise')} ${props.myIndex + 1}`)

// Play the introduction audio automatically
emit('start-introduction', true)

// Configure the output object at any time. It will be included when calling 'completed'

// Call this when the task is complete
const finish = async () => {
  await deviceActions.stopRecordingAudio()
  // Call this when the task is complete
  if (mode.value === MODES.discussion) {
    if (shuffledPlayers.length > 0 && currentPlayer.value && currentTracking) {
      currentTracking.audioFile = deviceGetters.audioFilename.value
      currentTracking.complete()
      gameStore.actions.commitNewTracking(currentTracking)
      changeMode(MODES.playerReady)
    } else emit('completed')
  }
  // Call when finishing early
  else emit('not-completed')
  stopSounds()
}

const playInstructionSound = (): void => {
  stopSounds()
  playSounds([question.value.fishingIntro])
}

// -------------    Seven Seas -----------------

const changeMode = (newMode: MODES) => {
  stopSounds()
  if (newMode === MODES.playerReady) {
    playSounds([yourTurn])
    deviceActions.createAudio().then(() => {
      deviceActions.startRecordingAudio()
    })
    if (shuffledPlayers.length > 0) currentPlayer.value = shuffledPlayers.pop()
    const game = gameStore.getters.selectedGame.value
    if (game && currentPlayer.value) {
      currentTracking = new TrackingDetails({
        itemID: props.question._id,
        gameID: game._id,
        playerIDs: [currentPlayer.value._id],
        projectID: game.project.id,
        type: TRACKING_TYPE.subtask,
        details: {
          user: userStore.getters.myUser.value.profile.username,
          taskType: TASK_TYPES.sevenseas,
          taskInfo: question.value.name,
          word,
          isCorrect: true,
        },
      })
    }
  } else if (newMode === MODES.fishing) {
    // Allocate a new 'item' to be fished out of the sea
    currentItem.value = items.value.pop()
    if (!introHeard.value) playSounds([question.value.fishingIntro])
    introHeard.value = true
  } else if (newMode === MODES.quizz) {
    // Item fished, show a quizz or go directly to Word Focus
    if (currentItem.value && !currentItem.value.question) mode.value = MODES.wordFocus
  } else if (newMode === MODES.discussion) {
    if (currentItem.value) playSounds([currentItem.value.discussionSound])
    // This player is done, try for the next player
  }
  mode.value = newMode
}

const __returned__ = { messages, MODES, emit, props, t, question, deviceGetters, deviceActions, mode, cmsStore, gameStore, userStore, selectedSet, word, items, currentItem, players, shuffledPlayers, currentPlayer, currentTracking, introHeard, exerciseIndex, finish, playInstructionSound, changeMode, Button, Avatar, Intro, PlayerReady, Fishing, ItemDetail, DiscussionTime, boatImage }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})